import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { fmtEur } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Building2, Plus, Trash2, Send, Check,
  FileText, ExternalLink, Search, Package as PackageIcon, ShieldAlert,
  ChevronDown,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { API_BASE } from "@/lib/api";
import PdfPreviewModal from "@/components/PdfPreviewModal";

const STEPS = [
  { key: "cliente", label: "Cliente" },
  { key: "servizi", label: "Servizi" },
  { key: "accessori", label: "Accessori & trasferte" },
  { key: "condizioni", label: "Sconti & condizioni" },
  { key: "riepilogo", label: "Riepilogo & invio" },
];

const ACC_TYPES = [
  { key: "trasferta", label: "Trasferta", unit: "ore", prezzo: 50 },
  { key: "carburante", label: "Carburante", unit: "km", prezzo: 0.5 },
  { key: "pasti", label: "Pasti", unit: "pasti", prezzo: 30 },
  { key: "pedaggi", label: "Pedaggi", unit: "forfait", prezzo: 0 },
  { key: "sopralluogo_sicurezza", label: "Sopralluogo sicurezza", unit: "ore", prezzo: 100 },
  { key: "sopralluogo_igiene", label: "Sopralluogo igiene alimentare", unit: "ore", prezzo: 100 },
  { key: "nota_riservata", label: "Nota riservata", unit: "ore", prezzo: 100 },
  { key: "altro", label: "Altro", unit: "forfait", prezzo: 0 },
];

export default function OfferWizardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(0);
  const [clients, setClients] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [packages, setPackages] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [offer, setOffer] = useState({
    client_id: "",
    lines: [],
    accessori: [],
    sconto_percentuale: 0,
    sconto_fisso: 0,
    iva_globale: 22,
    applica_iva: true,
    validita_giorni: 30,
    condizioni: "",
    modalita_pagamento: "Bonifico bancario a 30 giorni data fattura",
    durata_incarico: "12 mesi con tacito rinnovo",
    esclusioni: "",
    note_visibili: "",
    note_interne: "",
    margine_stimato: 0,
    costo_interno_stimato: 0,
  });
  const [offerId, setOfferId] = useState(id && id !== "nuova" ? id : null);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");
  const [catQ, setCatQ] = useState("");
  const [pkgOpen, setPkgOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewOfferId, setPreviewOfferId] = useState(null);
  const [previewOfferNumero, setPreviewOfferNumero] = useState("");

  useEffect(() => {
    api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
    api.get("/catalog").then(({ data }) => setCatalog(data.filter((c) => c.attivo))).catch(() => {});
    api.get("/packages").then(({ data }) => setPackages(data.filter((p) => p.attivo))).catch(() => {});
  }, []);

  useEffect(() => {
    if (offerId) {
      api.get(`/offers/${offerId}`).then(({ data }) => {
        setOffer({
          client_id: data.client_id, lines: data.lines || [], accessori: data.accessori || [],
          sconto_percentuale: data.sconto_percentuale || 0, sconto_fisso: data.sconto_fisso || 0,
          iva_globale: data.iva_globale || 22, applica_iva: data.applica_iva !== false,
          validita_giorni: data.validita_giorni || 30, condizioni: data.condizioni || "",
          modalita_pagamento: data.modalita_pagamento || "", durata_incarico: data.durata_incarico || "",
          esclusioni: data.esclusioni || "", note_visibili: data.note_visibili || "",
          note_interne: data.note_interne || "", margine_stimato: data.margine_stimato || 0,
          costo_interno_stimato: data.costo_interno_stimato || 0,
        });
        setSelectedClient(data.cliente);
      });
    }
  }, [offerId]);

  useEffect(() => {
    if (selectedClient?.id) setOffer((o) => ({ ...o, client_id: selectedClient.id }));
  }, [selectedClient]);

  // ---- Live calc ----
  const totals = useMemo(() => {
    const linesTot = offer.lines.reduce((s, l) => {
      if (l.escludi_da_totale) return s;
      return s + (Number(l.quantita) || 0) * (Number(l.prezzo_unitario) || 0);
    }, 0);
    const accTot = offer.accessori.reduce((s, a) => s + (Number(a.quantita) || 0) * (Number(a.prezzo_unitario) || 0), 0);
    const subtotale = linesTot + accTot;
    const sconto = subtotale * (Number(offer.sconto_percentuale) || 0) / 100 + (Number(offer.sconto_fisso) || 0);
    const imponibile = Math.max(subtotale - sconto, 0);
    const iva = offer.applica_iva ? imponibile * (Number(offer.iva_globale) || 0) / 100 : 0;
    const totale = imponibile + iva;
    return {
      subtotale, sconto, imponibile, iva, totale,
      mensile: totale / 12,
      linesTot, accTot,
    };
  }, [offer]);

  // ---- Save (create or update draft) ----
  const saveDraft = async (silent = false) => {
    if (!offer.client_id) {
      if (!silent) toast.error("Seleziona un cliente prima");
      return null;
    }
    setSaving(true);
    try {
      if (offerId) {
        const { data } = await api.patch(`/offers/${offerId}`, offer);
        if (!silent) toast.success("Bozza aggiornata");
        return data;
      } else {
        const { data } = await api.post("/offers", offer);
        setOfferId(data.id);
        if (!silent) toast.success(`Bozza creata: ${data.numero}`);
        return data;
      }
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Errore salvataggio");
      return null;
    } finally {
      setSaving(false);
    }
  };

  // ---- Actions ----
  const addLineFromCatalog = (cat) => {
    // Servizio con listino a scaglioni (Medicina del Lavoro): apri modale
    if (cat.pricing_type === "listino_dipendenti" && Array.isArray(cat.listino_dipendenti_tiers) && cat.listino_dipendenti_tiers.length > 0) {
      openTierPicker(cat);
      return;
    }
    let quantita = 1;
    if (cat.unita === "dipendente" && selectedClient?.numero_dipendenti) {
      quantita = selectedClient.numero_dipendenti;
    }
    if (cat.unita === "mese") quantita = 12;
    setOffer((o) => ({
      ...o,
      lines: [...o.lines, {
        id: crypto.randomUUID(),
        catalog_item_id: cat.id,
        nome: cat.nome,
        descrizione: cat.descrizione_breve || "",
        categoria: cat.categoria,
        quantita,
        prezzo_unitario: cat.prezzo_unitario,
        unita: cat.unita,
        iva: cat.iva,
        totale: quantita * cat.prezzo_unitario,
        escludi_pdf: false,
        personalizzata: false,
        escludi_da_totale: false,
        tier_label: "",
      }],
    }));
  };

  // ---- Tier picker (Medicina del Lavoro) ----
  const [tierPicker, setTierPicker] = useState({ open: false, cat: null });
  const openTierPicker = (cat) => {
    // Default: pick tier matching client's numero_dipendenti if any
    setTierPicker({ open: true, cat });
  };
  const closeTierPicker = () => setTierPicker({ open: false, cat: null });
  const applyTier = (cat, tier) => {
    setOffer((o) => ({
      ...o,
      lines: [...o.lines, {
        id: crypto.randomUUID(),
        catalog_item_id: cat.id,
        nome: cat.nome,
        descrizione: cat.descrizione_breve || "",
        categoria: cat.categoria,
        quantita: 1,
        prezzo_unitario: Number(tier.prezzo || 0),
        unita: cat.unita || "anno",
        iva: cat.iva,
        totale: Number(tier.prezzo || 0),
        escludi_pdf: false,
        personalizzata: false,
        escludi_da_totale: Boolean(cat.escludi_da_totale_default),
        tier_label: tier.label || "",
      }],
    }));
    closeTierPicker();
  };

  const addCustomLine = () => {
    setOffer((o) => ({
      ...o,
      lines: [...o.lines, {
        id: crypto.randomUUID(), catalog_item_id: null,
        nome: "Voce personalizzata", descrizione: "", categoria: "personalizzata",
        quantita: 1, prezzo_unitario: 0, unita: "forfait", iva: 22, totale: 0,
        escludi_pdf: false, personalizzata: true,
      }],
    }));
  };

  const updateLine = (id, k, v) => setOffer((o) => ({
    ...o,
    lines: o.lines.map((l) => l.id === id ? { ...l, [k]: v, totale: (k === "quantita" || k === "prezzo_unitario") ? (Number(k === "quantita" ? v : l.quantita) * Number(k === "prezzo_unitario" ? v : l.prezzo_unitario)) : l.totale } : l),
  }));
  const rmLine = (id) => setOffer((o) => ({ ...o, lines: o.lines.filter((l) => l.id !== id) }));
  const rmLineByCatalog = (catalogId) => setOffer((o) => ({ ...o, lines: o.lines.filter((l) => l.catalog_item_id !== catalogId) }));

  // Group catalog items by category, filtered by search
  const catalogGroups = useMemo(() => {
    const q = catQ.trim().toLowerCase();
    const filtered = catalog.filter((c) => {
      if (!q) return true;
      return (c.nome || "").toLowerCase().includes(q)
          || (c.categoria || "").toLowerCase().includes(q)
          || (c.descrizione_breve || "").toLowerCase().includes(q);
    });
    // Exclude the "Accessori" category from the services step (they belong to next step)
    const services = filtered.filter((c) => (c.categoria || "").toLowerCase() !== "accessori");
    return services.reduce((acc, c) => {
      const cat = c.categoria || "Altro";
      (acc[cat] = acc[cat] || []).push(c);
      return acc;
    }, {});
  }, [catalog, catQ]);

  const addAccessorio = (tipo) => {
    const def = ACC_TYPES.find((a) => a.key === tipo);
    setOffer((o) => ({
      ...o,
      accessori: [...o.accessori, {
        id: crypto.randomUUID(),
        tipo, descrizione: def.label,
        quantita: 0, prezzo_unitario: def.prezzo,
        totale: 0, note: "",
      }],
    }));
  };
  const updAcc = (id, k, v) => setOffer((o) => ({
    ...o,
    accessori: o.accessori.map((a) => a.id === id ? { ...a, [k]: v, totale: (Number(k === "quantita" ? v : a.quantita) * Number(k === "prezzo_unitario" ? v : a.prezzo_unitario)) } : a),
  }));
  const rmAcc = (id) => setOffer((o) => ({ ...o, accessori: o.accessori.filter((a) => a.id !== id) }));

  const applyPackage = (pkg) => {
    if (!pkg?.items) return;
    const newLines = pkg.items.map((it) => {
      const cat = catalog.find((c) => c.id === it.catalog_item_id);
      if (!cat) return null;
      let q = it.quantita || 1;
      if (cat.unita === "dipendente" && selectedClient?.numero_dipendenti) q = selectedClient.numero_dipendenti;
      if (cat.unita === "mese" && q === 1) q = 12;
      return {
        id: crypto.randomUUID(), catalog_item_id: cat.id,
        nome: cat.nome, descrizione: cat.descrizione_breve || "", categoria: cat.categoria,
        quantita: q, prezzo_unitario: cat.prezzo_unitario, unita: cat.unita,
        iva: cat.iva, totale: q * cat.prezzo_unitario, escludi_pdf: false, personalizzata: false,
      };
    }).filter(Boolean);
    setOffer((o) => ({ ...o, lines: [...o.lines, ...newLines] }));
    toast.success(`Pacchetto "${pkg.nome}" applicato (${newLines.length} voci)`);
  };

  const importSediAccessori = () => {
    if (!selectedClient?.sedi?.length) return toast.error("Il cliente non ha sedi operative");
    const nuovi = [];
    selectedClient.sedi.forEach((s) => {
      const accessi = Number(s.accessi_annui) || 1;
      if (s.ore_trasferta > 0) nuovi.push({ id: crypto.randomUUID(), tipo: "trasferta", descrizione: `Trasferta ${s.nome_sede || s.comune} (${accessi} accessi)`, quantita: Number(s.ore_trasferta) * accessi, prezzo_unitario: 50, totale: Number(s.ore_trasferta) * accessi * 50, note: "", sede_id: s.id });
      if (s.km_da_csc > 0) nuovi.push({ id: crypto.randomUUID(), tipo: "carburante", descrizione: `Carburante A/R ${s.nome_sede || s.comune} (${accessi} accessi)`, quantita: Number(s.km_da_csc) * accessi, prezzo_unitario: 0.5, totale: Number(s.km_da_csc) * accessi * 0.5, note: "", sede_id: s.id });
      if (s.pasti > 0) nuovi.push({ id: crypto.randomUUID(), tipo: "pasti", descrizione: `Pasti ${s.nome_sede || s.comune}`, quantita: Number(s.pasti), prezzo_unitario: 30, totale: Number(s.pasti) * 30, note: "", sede_id: s.id });
      if (s.pedaggi > 0) nuovi.push({ id: crypto.randomUUID(), tipo: "pedaggi", descrizione: `Pedaggi ${s.nome_sede || s.comune}`, quantita: 1, prezzo_unitario: Number(s.pedaggi) * accessi, totale: Number(s.pedaggi) * accessi, note: "", sede_id: s.id });
    });
    if (!nuovi.length) return toast.error("Nessun valore da importare dalle sedi");
    setOffer((o) => ({ ...o, accessori: [...o.accessori, ...nuovi] }));
    toast.success(`${nuovi.length} righe importate dalle sedi`);
  };

  const submitForApproval = async () => {
    const saved = await saveDraft(true);
    if (!saved) return;
    try {
      await api.post(`/offers/${saved.id}/submit`);
      toast.success("Offerta inviata in approvazione");
      navigate(`/offerte/${saved.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const approveDirectly = async () => {
    const saved = await saveDraft(true);
    if (!saved) return;
    try {
      await api.post(`/offers/${saved.id}/submit`);
      await api.post(`/offers/${saved.id}/approve`, { commento: "Approvazione diretta" });
      toast.success("Offerta approvata");
      navigate(`/offerte/${saved.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const canNext = () => {
    if (step === 0) return !!offer.client_id;
    if (step === 1) return offer.lines.length > 0;
    return true;
  };

  return (
    <div className="pb-24">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="p-2 rounded hover:bg-slate-100" data-testid="wizard-back">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono">Crea offerta</div>
          <h1 className="font-display text-2xl font-bold text-[#0F2C59]">
            {offerId ? `Offerta bozza` : "Nuova offerta"}
          </h1>
        </div>
      </div>

      {/* Step indicator - sticky on mobile */}
      <div className="mb-6 sticky top-16 z-10 -mx-4 md:mx-0 px-4 md:px-0 py-2 md:py-0 bg-slate-50/95 backdrop-blur md:bg-transparent md:backdrop-blur-none">
        <div className="flex items-center gap-1.5 md:gap-2 overflow-x-auto pb-1">
          {STEPS.map((s, i) => {
            const state = i < step ? "done" : i === step ? "current" : "next";
            return (
              <button
                key={s.key}
                onClick={() => setStep(i)}
                data-testid={`step-${s.key}`}
                className={`step-indicator flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex-shrink-0 ${
                  state === "current" ? "bg-[#FACC15] text-[#0F2C59] border-[#FACC15] shadow-sm" :
                  state === "done" ? "bg-[#0F2C59] text-white border-[#0F2C59]" :
                  "bg-white text-slate-500 border-slate-300"
                }`}
              >
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold bg-white/30 flex-shrink-0">
                  {state === "done" ? <Check className="w-3 h-3" strokeWidth={3} /> : i + 1}
                </span>
                <span className={`${state === "current" ? "inline" : "hidden md:inline"}`}>{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Mobile: mini total bar */}
      <div className="lg:hidden mb-4 -mx-4 px-4 py-3 bg-gradient-to-r from-[#0F2C59] to-[#173F7A] text-white rounded-none">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/60 font-mono">Totale offerta</div>
            <div className="text-xl font-display font-bold" data-testid="wiz-total-mobile">{fmtEur(totals.totale)}</div>
          </div>
          <div className="text-right text-xs text-white/70">
            <div>{offer.lines.length} servizi · {offer.accessori.length} accessori</div>
            <div className="text-[#FACC15] font-mono font-semibold">≈ {fmtEur(totals.mensile)}/mese</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Main panel */}
        <div className="lg:col-span-8 space-y-4">
          {/* ============ STEP 0: CLIENTE ============ */}
          {step === 0 && (
            <Card className="p-6 space-y-4">
              <div>
                <div className="font-display font-semibold text-[#0F2C59] text-lg">1. Selezione cliente</div>
                <p className="text-sm text-slate-500 mt-1">Scegli un cliente esistente oppure creane uno nuovo.</p>
              </div>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input placeholder="Cerca cliente…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10" data-testid="wiz-client-search" />
                </div>
                <Button variant="outline" onClick={() => navigate("/clienti/nuovo")} data-testid="wiz-new-client">
                  <Plus className="w-4 h-4 mr-1" /> Nuovo cliente
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-[500px] overflow-y-auto pr-1">
                {clients
                  .filter((c) => !q || c.ragione_sociale.toLowerCase().includes(q.toLowerCase()) || (c.partita_iva || "").includes(q))
                  .map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setSelectedClient(c)}
                      data-testid={`wiz-select-${c.id}`}
                      className={`text-left p-4 rounded-lg border transition-all ${
                        selectedClient?.id === c.id
                          ? "border-[#0F2C59] bg-[#E6F0FF] ring-2 ring-[#0F2C59]/20"
                          : "border-slate-200 bg-white hover:border-[#0F2C59]"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <Building2 className="w-5 h-5 text-[#0F2C59] mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{c.ragione_sociale}</div>
                          <div className="text-xs text-slate-500 mt-0.5">{c.comune} · {c.numero_dipendenti} dip. · rischio {c.livello_rischio}</div>
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
              {selectedClient && (
                <div className="mt-4 p-4 bg-[#E6F0FF] rounded-lg border border-[#0F2C59]/20">
                  <div className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-[#0F2C59]" />
                    <span>Cliente selezionato: <strong>{selectedClient.ragione_sociale}</strong></span>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* ============ STEP 1: SERVIZI (voci singole cliccabili) ============ */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Client info compact banner */}
              {selectedClient && (
                <Card className="p-4 flex flex-wrap items-center gap-4 bg-slate-50 border-slate-200">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#0F2C59]" />
                    <div>
                      <div className="font-display font-semibold text-[#0F2C59]">{selectedClient.ragione_sociale}</div>
                      <div className="text-xs text-slate-500">
                        {selectedClient.numero_dipendenti} dip · ATECO {selectedClient.codice_ateco || "—"} · {selectedClient.sedi?.length || 0} sedi
                      </div>
                    </div>
                  </div>
                  <div className="flex-1" />
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${
                    selectedClient.livello_rischio === "alto" ? "bg-red-50 text-red-700 border-red-300" :
                    selectedClient.livello_rischio === "medio" ? "bg-orange-50 text-orange-700 border-orange-300" :
                    "bg-green-50 text-green-700 border-green-300"
                  }`}>Rischio {selectedClient.livello_rischio}</span>
                  {selectedClient.igiene_alimentare && <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700 border border-cyan-300">HACCP</span>}
                  {selectedClient.piscina_impianti && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-300">Piscina</span>}
                </Card>
              )}

              {/* Optional: apply package as shortcut - collapsed accordion */}
              {packages.length > 0 && (
                <Card className="p-0 overflow-hidden">
                  <button
                    onClick={() => setPkgOpen((v) => !v)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 text-left"
                    data-testid="toggle-packages"
                  >
                    <div className="flex items-center gap-2">
                      <PackageIcon className="w-4 h-4 text-[#0F2C59]" />
                      <span className="font-medium text-sm">Applica un pacchetto pre-configurato</span>
                      <span className="text-xs text-slate-400">(opzionale)</span>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${pkgOpen ? "rotate-180" : ""}`} />
                  </button>
                  {pkgOpen && (
                    <div className="p-4 pt-0 border-t border-slate-200">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 pt-3">
                        {packages.map((p) => (
                          <button
                            key={p.id}
                            onClick={() => applyPackage(p)}
                            data-testid={`apply-package-${p.id}`}
                            className="text-left p-3 border border-slate-200 rounded-md hover:border-[#0F2C59] hover:bg-slate-50 transition-colors"
                          >
                            <div className="font-medium text-sm text-[#0F2C59]">{p.nome}</div>
                            <div className="text-xs text-slate-500 mt-0.5">{p.items?.length || 0} voci incluse</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              )}

              {/* Individual services grouped by category (clickable to add/remove) */}
              <Card className="p-6 space-y-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-display font-semibold text-[#0F2C59] text-lg">Scegli i servizi</div>
                    <p className="text-sm text-slate-500 mt-1">Clicca una voce per aggiungerla; ricliccala per rimuoverla. Modifica prezzo e quantità sotto.</p>
                  </div>
                  <div className="flex gap-2">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <Input
                        placeholder="Cerca servizio…"
                        value={catQ}
                        onChange={(e) => setCatQ(e.target.value)}
                        className="pl-9 w-52 h-9 text-sm"
                        data-testid="catalog-search"
                      />
                    </div>
                    <Button variant="outline" size="sm" onClick={addCustomLine} data-testid="add-custom-line">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Personalizzata
                    </Button>
                  </div>
                </div>

                {Object.entries(catalogGroups).length === 0 && (
                  <div className="text-slate-400 text-sm text-center py-8">Nessun servizio nel listino</div>
                )}
                {Object.entries(catalogGroups).map(([categoria, list]) => (
                  <div key={categoria}>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-2 pb-1 border-b border-slate-100">
                      {categoria}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {list.map((c) => {
                        const selected = offer.lines.some((l) => l.catalog_item_id === c.id);
                        return (
                          <button
                            key={c.id}
                            onClick={() => selected ? rmLineByCatalog(c.id) : addLineFromCatalog(c)}
                            data-testid={`add-catalog-${c.id}`}
                            className={`text-left p-3 border rounded-md transition-all flex justify-between items-center gap-3 ${
                              selected
                                ? "border-[#0F2C59] bg-[#E6F0FF] ring-1 ring-[#0F2C59]/30"
                                : "border-slate-200 bg-white hover:border-[#0F2C59] hover:bg-slate-50"
                            }`}
                          >
                            <div className="flex items-start gap-2 min-w-0">
                              <div className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border ${
                                selected ? "bg-[#0F2C59] border-[#0F2C59] text-white" : "border-slate-300 bg-white"
                              }`}>
                                {selected && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium truncate">{c.nome}</div>
                                {c.descrizione_breve && <div className="text-xs text-slate-500 truncate">{c.descrizione_breve}</div>}
                                <div className="text-xs text-slate-400 mt-0.5">/ {c.unita}</div>
                              </div>
                            </div>
                            <div className="text-sm font-mono text-[#0F2C59] whitespace-nowrap font-semibold">{fmtEur(c.prezzo_unitario)}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </Card>

              {/* Selected lines editor */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display font-semibold text-[#0F2C59]">Voci selezionate ({offer.lines.length})</div>
                  {offer.lines.length > 0 && (
                    <div className="text-sm text-slate-500">
                      Totale servizi: <span className="font-mono font-semibold text-[#0F2C59]">{fmtEur(totals.linesTot)}</span>
                    </div>
                  )}
                </div>
                {offer.lines.length === 0 ? (
                  <div className="text-center py-10 text-slate-400 border border-dashed rounded-md">
                    Nessuna voce selezionata — clicca un servizio sopra per aggiungerlo
                  </div>
                ) : (
                  <div className="space-y-2">
                    {offer.lines.map((l) => (
                      <div key={l.id} className="grid grid-cols-12 gap-2 items-center p-3 border border-slate-200 rounded-md bg-white">
                        <div className="col-span-12 md:col-span-4">
                          <Input className="text-sm" value={l.nome} onChange={(e) => updateLine(l.id, "nome", e.target.value)} data-testid={`line-nome-${l.id}`} />
                          {(l.tier_label || l.escludi_da_totale) && (
                            <div className="mt-1 flex items-center gap-1 flex-wrap">
                              {l.tier_label && (
                                <span data-testid={`line-tier-${l.id}`} className="inline-flex items-center rounded-full bg-blue-50 text-[#0F2C59] px-2 py-0.5 text-[10px] font-semibold border border-blue-200">
                                  {l.tier_label}
                                </span>
                              )}
                              {l.escludi_da_totale && (
                                <span className="inline-flex items-center rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-[10px] font-medium border border-amber-200">
                                  non incluso nel totale
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="col-span-6 md:col-span-2">
                          <div className="text-[10px] text-slate-400 uppercase font-mono">Q.tà ({l.unita})</div>
                          <Input className="text-sm h-8" type="number" step="0.01" value={l.quantita} onChange={(e) => updateLine(l.id, "quantita", Number(e.target.value))} data-testid={`line-qty-${l.id}`} />
                        </div>
                        <div className="col-span-6 md:col-span-2">
                          <div className="text-[10px] text-slate-400 uppercase font-mono">Prezzo €</div>
                          <Input className="text-sm h-8" type="number" step="0.01" value={l.prezzo_unitario} onChange={(e) => updateLine(l.id, "prezzo_unitario", Number(e.target.value))} data-testid={`line-price-${l.id}`} />
                        </div>
                        <div className="col-span-8 md:col-span-3 text-right font-mono text-sm font-semibold text-[#0F2C59]">
                          {fmtEur((Number(l.quantita) || 0) * (Number(l.prezzo_unitario) || 0))}
                        </div>
                        <button onClick={() => rmLine(l.id)} className="col-span-4 md:col-span-1 p-1.5 rounded hover:bg-red-50 text-red-500 flex justify-center" data-testid={`rm-line-${l.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            </div>
          )}

          {/* ============ STEP 2: ACCESSORI ============ */}
          {step === 2 && (
            <Card className="p-6 space-y-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="font-display font-semibold text-[#0F2C59] text-lg">Costi accessori & trasferte</div>
                  <p className="text-sm text-slate-500 mt-1">Aggiungi costi trasferte, carburante, pasti, sopralluoghi. Importa dalle sedi cliente per calcolo automatico.</p>
                </div>
                <Button variant="outline" onClick={importSediAccessori} data-testid="import-sedi-btn">
                  <Building2 className="w-4 h-4 mr-1" /> Importa dalle sedi
                </Button>
              </div>

              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-2">Aggiungi accessorio</div>
                <div className="flex flex-wrap gap-2">
                  {ACC_TYPES.map((t) => (
                    <Button key={t.key} variant="outline" size="sm" onClick={() => addAccessorio(t.key)} data-testid={`add-acc-${t.key}`}>
                      + {t.label}
                    </Button>
                  ))}
                </div>
              </div>

              {offer.accessori.length === 0 ? (
                <div className="text-center py-10 text-slate-400 border border-dashed rounded-md">Nessun accessorio</div>
              ) : (
                <div className="space-y-2">
                  {offer.accessori.map((a) => (
                    <div key={a.id} className="grid grid-cols-12 gap-2 items-center p-3 border rounded-md">
                      <div className="col-span-12 md:col-span-3">
                        <div className="text-xs text-slate-500 uppercase font-mono">{a.tipo.replace("_", " ")}</div>
                        <Input value={a.descrizione} onChange={(e) => updAcc(a.id, "descrizione", e.target.value)} className="mt-1 text-sm" />
                      </div>
                      <Input className="col-span-6 md:col-span-2 text-sm" type="number" step="0.01" value={a.quantita} onChange={(e) => updAcc(a.id, "quantita", Number(e.target.value))} placeholder="Quantità" data-testid={`acc-qty-${a.id}`} />
                      <Input className="col-span-6 md:col-span-2 text-sm" type="number" step="0.01" value={a.prezzo_unitario} onChange={(e) => updAcc(a.id, "prezzo_unitario", Number(e.target.value))} placeholder="Prezzo" data-testid={`acc-price-${a.id}`} />
                      <Input className="col-span-8 md:col-span-3 text-sm" value={a.note} onChange={(e) => updAcc(a.id, "note", e.target.value)} placeholder="Note" />
                      <div className="col-span-4 md:col-span-1 text-right font-mono text-sm text-[#0F2C59]">
                        {fmtEur((Number(a.quantita) || 0) * (Number(a.prezzo_unitario) || 0))}
                      </div>
                      <button onClick={() => rmAcc(a.id)} className="col-span-12 md:col-span-1 p-1.5 rounded hover:bg-red-50 text-red-500 flex justify-center">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}

          {/* ============ STEP 3: SCONTI & CONDIZIONI ============ */}
          {step === 3 && (
            <Card className="p-6 space-y-6">
              <div>
                <div className="font-display font-semibold text-[#0F2C59] text-lg">Sconti, IVA e condizioni</div>
                <p className="text-sm text-slate-500 mt-1">Applica sconti, imposta IVA e definisci le condizioni commerciali.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label>Sconto %</Label>
                  <Input type="number" step="0.01" value={offer.sconto_percentuale} onChange={(e) => setOffer({ ...offer, sconto_percentuale: Number(e.target.value) })} className="mt-1.5" data-testid="sconto-pct" />
                </div>
                <div>
                  <Label>Sconto fisso (€)</Label>
                  <Input type="number" step="0.01" value={offer.sconto_fisso} onChange={(e) => setOffer({ ...offer, sconto_fisso: Number(e.target.value) })} className="mt-1.5" data-testid="sconto-fix" />
                </div>
                <div>
                  <Label>IVA (%)</Label>
                  <Input type="number" step="0.01" value={offer.iva_globale} onChange={(e) => setOffer({ ...offer, iva_globale: Number(e.target.value) })} className="mt-1.5" />
                </div>
                <div>
                  <Label>Applica IVA</Label>
                  <div className="mt-3 flex items-center gap-2">
                    <Checkbox checked={offer.applica_iva} onCheckedChange={(v) => setOffer({ ...offer, applica_iva: !!v })} data-testid="applica-iva" />
                    <span className="text-sm">Sì, aggiungi IVA</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Validità offerta (giorni)</Label>
                  <Input type="number" value={offer.validita_giorni} onChange={(e) => setOffer({ ...offer, validita_giorni: Number(e.target.value) })} className="mt-1.5" data-testid="validita" />
                </div>
                <div>
                  <Label>Modalità di pagamento</Label>
                  <Input value={offer.modalita_pagamento} onChange={(e) => setOffer({ ...offer, modalita_pagamento: e.target.value })} className="mt-1.5" />
                </div>
                <div className="md:col-span-2">
                  <Label>Durata incarico</Label>
                  <Input value={offer.durata_incarico} onChange={(e) => setOffer({ ...offer, durata_incarico: e.target.value })} className="mt-1.5" />
                </div>
                <div className="md:col-span-2">
                  <Label>Esclusioni</Label>
                  <Textarea value={offer.esclusioni} onChange={(e) => setOffer({ ...offer, esclusioni: e.target.value })} rows={2} className="mt-1.5" />
                </div>
                <div className="md:col-span-2">
                  <Label>Note visibili al cliente</Label>
                  <Textarea value={offer.note_visibili} onChange={(e) => setOffer({ ...offer, note_visibili: e.target.value })} rows={3} className="mt-1.5" data-testid="note-visibili" />
                </div>
                <div className="md:col-span-2">
                  <Label className="flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-orange-500" /> Note interne (non visibili al cliente)</Label>
                  <Textarea value={offer.note_interne} onChange={(e) => setOffer({ ...offer, note_interne: e.target.value })} rows={3} className="mt-1.5" data-testid="note-interne" />
                </div>
                <div>
                  <Label>Costo interno stimato (€)</Label>
                  <Input type="number" value={offer.costo_interno_stimato} onChange={(e) => setOffer({ ...offer, costo_interno_stimato: Number(e.target.value) })} className="mt-1.5" />
                </div>
                <div>
                  <Label>Margine stimato (€)</Label>
                  <Input type="number" value={offer.margine_stimato} onChange={(e) => setOffer({ ...offer, margine_stimato: Number(e.target.value) })} className="mt-1.5" />
                </div>
              </div>
            </Card>
          )}

          {/* ============ STEP 4: RIEPILOGO ============ */}
          {step === 4 && (
            <Card className="p-6 space-y-6">
              <div>
                <div className="font-display font-semibold text-[#0F2C59] text-lg">Riepilogo & invio</div>
                <p className="text-sm text-slate-500 mt-1">Controlla i totali, anteprima PDF e invia in approvazione.</p>
              </div>

              <div className="border rounded-lg p-4 bg-slate-50">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono mb-2">Voci offerta</div>
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500">
                    <tr><th className="text-left py-1">Servizio</th><th className="text-right py-1">Q.tà</th><th className="text-right py-1">Prezzo</th><th className="text-right py-1">Totale</th></tr>
                  </thead>
                  <tbody>
                    {offer.lines.map((l) => (
                      <tr key={l.id} className="border-t border-slate-200">
                        <td className="py-1.5">{l.nome}</td>
                        <td className="text-right font-mono">{l.quantita} {l.unita}</td>
                        <td className="text-right font-mono">{fmtEur(l.prezzo_unitario)}</td>
                        <td className="text-right font-mono">{fmtEur((Number(l.quantita) || 0) * (Number(l.prezzo_unitario) || 0))}</td>
                      </tr>
                    ))}
                    {offer.accessori.filter((a) => a.totale > 0 || (a.quantita * a.prezzo_unitario) > 0).map((a) => (
                      <tr key={a.id} className="border-t border-slate-200 text-slate-600">
                        <td className="py-1.5 italic">{a.descrizione}</td>
                        <td className="text-right font-mono">{a.quantita}</td>
                        <td className="text-right font-mono">{fmtEur(a.prezzo_unitario)}</td>
                        <td className="text-right font-mono">{fmtEur((Number(a.quantita) || 0) * (Number(a.prezzo_unitario) || 0))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  onClick={async () => {
                    const s = await saveDraft(true);
                    if (!s) return;
                    const url = `${API_BASE}/offers/${s.id}/pdf?inline=true`;
                    const w = window.open(url, "_blank");
                    if (!w) window.location.href = url;
                  }}
                  data-testid="preview-pdf-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-1" /> Anteprima PDF
                </Button>
                <Button variant="outline" onClick={async () => {
                  const s = await saveDraft(true);
                  if (!s) return;
                  const url = `${API_BASE}/offers/${s.id}/pdf`;
                  const w = window.open(url, "_blank");
                  if (!w) window.location.href = url;
                }} data-testid="download-pdf-wizard">
                  <FileText className="w-4 h-4 mr-1" /> Scarica PDF
                </Button>
                <Button variant="outline" onClick={() => saveDraft()} data-testid="save-draft-btn">
                  Salva come bozza
                </Button>
                <Button className="bg-[#0F2C59] hover:bg-[#173F7A]" onClick={submitForApproval} data-testid="submit-approval-btn">
                  <Send className="w-4 h-4 mr-1" /> Invia in approvazione
                </Button>
                {(user?.role === "admin" || user?.role === "direzione") && (
                  <Button className="bg-[#FACC15] hover:bg-[#EAB308] text-[#0F2C59] font-semibold" onClick={approveDirectly} data-testid="approve-direct-btn">
                    <Check className="w-4 h-4 mr-1" /> Approva direttamente
                  </Button>
                )}
              </div>
            </Card>
          )}

          {/* Nav */}
          <div className="flex justify-between pt-4">
            <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="prev-step-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
            </Button>
            {step < STEPS.length - 1 ? (
              <Button
                className="bg-[#0F2C59] hover:bg-[#173F7A]"
                disabled={!canNext()}
                onClick={async () => {
                  if (step === 0) await saveDraft(true);
                  setStep((s) => s + 1);
                }}
                data-testid="next-step-btn"
              >
                Avanti <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : null}
          </div>
        </div>

        {/* Summary sidebar */}
        <aside className="lg:col-span-4">
          <div className="sticky top-24">
            <Card className="p-6 bg-white">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">Totale offerta</div>
              <div className="mt-3 font-display text-4xl font-bold text-[#0F2C59] tracking-tight" data-testid="wiz-total">
                {fmtEur(totals.totale)}
              </div>
              <div className="text-xs text-slate-500 mt-1">≈ {fmtEur(totals.mensile)} / mese</div>

              <div className="mt-6 space-y-1.5 text-sm">
                <Row label="Servizi" value={fmtEur(totals.linesTot)} />
                <Row label="Accessori" value={fmtEur(totals.accTot)} />
                <div className="border-t pt-1.5 mt-1.5" />
                <Row label="Subtotale" value={fmtEur(totals.subtotale)} />
                <Row label="Sconto" value={`- ${fmtEur(totals.sconto)}`} />
                <Row label="Imponibile" value={fmtEur(totals.imponibile)} bold />
                <Row label={`IVA ${offer.applica_iva ? offer.iva_globale + "%" : ""}`} value={fmtEur(totals.iva)} />
              </div>

              <div className="mt-6 pt-4 border-t space-y-2 text-xs text-slate-500">
                <div>Righe: <span className="font-mono text-slate-800">{offer.lines.length}</span></div>
                <div>Accessori: <span className="font-mono text-slate-800">{offer.accessori.length}</span></div>
                {selectedClient && <div>Cliente: <span className="text-slate-800">{selectedClient.ragione_sociale}</span></div>}
              </div>
            </Card>
          </div>
        </aside>
      </div>
      <PdfPreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        filename={`${previewOfferNumero || "offerta"}.pdf`}
        title={`Anteprima ${previewOfferNumero || ""}`}
        fetchBlob={async () => {
          const r = await api.get(`/offers/${previewOfferId}/pdf`, { responseType: "blob" });
          return new Blob([r.data], { type: "application/pdf" });
        }}
      />

      {/* Tier picker per servizi con listino a scaglioni (Medicina del Lavoro) */}
      <Dialog open={tierPicker.open} onOpenChange={(v) => !v && closeTierPicker()}>
        <DialogContent data-testid="tier-picker-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Seleziona scaglione dipendenti</DialogTitle>
            <DialogDescription>
              Scegli lo scaglione in base al numero di dipendenti dell&apos;azienda. Il prezzo indicato è informativo:
              non verrà sommato al totale dell&apos;offerta e sarà definito con i servizi aggiuntivi
              (spirometria, audiometria, ecc.).
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            {(tierPicker.cat?.listino_dipendenti_tiers || []).map((tier) => {
              const nDip = selectedClient?.numero_dipendenti || 0;
              const isMatch = nDip >= (tier.min_dipendenti || 0) && (tier.max_dipendenti == null || nDip <= tier.max_dipendenti);
              return (
                <button
                  key={tier.label}
                  data-testid={`tier-option-${tier.label.replace(/\s+/g, '-').replace(/\+/g, 'plus').toLowerCase()}`}
                  onClick={() => applyTier(tierPicker.cat, tier)}
                  className={`flex items-center justify-between rounded-lg border px-4 py-3 text-left transition hover:border-primary hover:bg-primary/5 ${isMatch ? 'border-primary bg-primary/5' : 'border-slate-200'}`}
                >
                  <div>
                    <div className="font-semibold text-slate-900">{tier.label}</div>
                    {isMatch && (
                      <div className="text-xs text-primary mt-0.5">Consigliato per {nDip} dipendenti</div>
                    )}
                  </div>
                  <div className="text-lg font-bold text-primary">€ {Number(tier.prezzo).toFixed(0)}</div>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeTierPicker} data-testid="tier-picker-cancel">Annulla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function InfoItem({ label, value, valueClass = "" }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">{label}</div>
      <div className={`font-medium mt-0.5 ${valueClass}`}>{value}</div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex justify-between">
      <span className={bold ? "font-semibold" : "text-slate-600"}>{label}</span>
      <span className={`font-mono ${bold ? "font-semibold" : ""}`}>{value}</span>
    </div>
  );
}
